// ===========================================
// Public Event Detail Page
// ===========================================
// Single public event by slug — shows full details, registration form (lead-capture)
// or paid-ticket checkout depending on event type. No login required.
// Route: /:schoolSlug/events/:eventSlug  (registered in App.tsx)

import { useState, lazy, Suspense } from 'react';
import { useParams, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Paper,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  EventAvailable as EventAvailableIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  ConfirmationNumber as TicketIcon,
  HowToReg as RegisterIcon,
  IosShare as ShareIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { usePublicEvent, useRegisterForEvent, usePublicTicketCheckout } from '../../hooks/useEvents';
import { eventIcsUrl, googleCalendarEventUrl } from '../../api/events.api';
import type { TicketCheckoutResult, EventTicketTierSummary } from '../../api/events.api';
import {
  TierQuantityPicker,
  tierPriceSummary,
  tierOrderTotalCents,
  quantitiesToItems,
  formatCents,
} from '../../components/events/EventTierPricing';
import PublicFooter from '../../components/public/PublicFooter';
import {
  EVENT_TYPE_COLORS,
  EVENT_TYPE_LABELS,
  eventTypeChipTextColor,
} from '../../constants/eventTypes';
import { formatAddress } from '../../constants/countries';

// Lazy-load the map so mapbox-gl stays out of the main bundle.
const VenueMap = lazy(() => import('../../components/events/VenueMap'));

function formatFullDate(start: string, end: string): string {
  const s = parseISO(start);
  const e = parseISO(end);
  return `${format(s, 'EEEE d MMMM yyyy')} · ${format(s, 'h:mm a')} – ${format(e, 'h:mm a')}`;
}

// ===========================================
// REGISTRATION FORM  (isPublic lead-capture)
// ===========================================

const registrationSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(50),
  lastName: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email address'),
  phone: z.string().max(30).optional().or(z.literal('')),
  guestCount: z
    .number({ error: 'Must be a number' })
    .min(1, 'Minimum 1')
    .max(20, 'Maximum 20')
    .optional(),
  website: z.string().optional(), // honeypot
});

type RegistrationFormData = z.infer<typeof registrationSchema>;

interface RegistrationFormProps {
  schoolSlug: string;
  eventSlug: string;
}

function RegistrationForm({ schoolSlug, eventSlug }: RegistrationFormProps) {
  const [submitted, setSubmitted] = useState(false);
  const registerMutation = useRegisterForEvent();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      guestCount: 1,
      website: '',
    },
  });

  const emailValue = watch('email');

  const onSubmit = async (data: RegistrationFormData) => {
    // Reject bots: honeypot must be empty
    if (data.website) return;

    try {
      await registerMutation.mutateAsync({
        slug: schoolSlug,
        eventSlug,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone || undefined,
          guestCount: data.guestCount ?? 1,
          website: data.website || undefined,
        },
      });
      setSubmitted(true);
    } catch {
      // Error handled by mutation's onError snackbar
    }
  };

  if (submitted) {
    return (
      <Paper
        sx={{
          p: 3,
          textAlign: 'center',
          border: '2px solid',
          borderColor: 'success.main',
          bgcolor: 'transparent',
        }}
      >
        <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          You're registered!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We've received your registration for this event. A confirmation will be sent to{' '}
          <strong>{emailValue}</strong>.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        border: '2px solid',
        borderColor: '#96DAC9',
        bgcolor: 'rgba(150, 218, 201, 0.10)',
        boxShadow: 'none',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <RegisterIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Register for this event
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {/* Honeypot — hidden from real users, bots will fill it */}
        <Controller
          name="website"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Website"
              sx={{ display: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
              autoComplete="off"
            />
          )}
        />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  fullWidth
                  required
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  fullWidth
                  required
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email Address"
              type="email"
              fullWidth
              required
              sx={{ mb: 2 }}
              error={!!errors.email}
              helperText={errors.email?.message || "We'll send a confirmation to this address"}
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Phone Number (Optional)"
              fullWidth
              placeholder="04XX XXX XXX"
              sx={{ mb: 2 }}
              error={!!errors.phone}
              helperText={errors.phone?.message}
            />
          )}
        />

        <Controller
          name="guestCount"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Number of Guests (including you)"
              type="number"
              fullWidth
              inputProps={{ min: 1, max: 20 }}
              onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
              sx={{ mb: 3 }}
              error={!!errors.guestCount}
              helperText={errors.guestCount?.message}
            />
          )}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={!isValid || registerMutation.isPending}
        >
          {registerMutation.isPending ? 'Registering...' : 'Register Now'}
        </Button>
      </Box>
    </Paper>
  );
}

// ===========================================
// TICKET CHECKOUT FORM  (paid event)
// ===========================================

const ticketHolderSchema = z.object({
  holderName: z.string().min(1, 'Name is required').max(100),
  holderEmail: z.string().email('Invalid email address'),
});

type TicketHolderData = z.infer<typeof ticketHolderSchema>;

interface TicketCheckoutFormProps {
  schoolSlug: string;
  eventSlug: string;
  tiers: EventTicketTierSummary[];
  /** Legacy single-price fallback for pre-tier events. */
  ticketPriceCents: number | null;
}

function TicketCheckoutForm({ schoolSlug, eventSlug, tiers, ticketPriceCents }: TicketCheckoutFormProps) {
  const checkoutMutation = usePublicTicketCheckout();
  const hasTiers = tiers.length > 0;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<TicketHolderData>({
    resolver: zodResolver(ticketHolderSchema),
    mode: 'onChange',
    defaultValues: { holderName: '', holderEmail: '' },
  });

  // Tier path: per-tier quantities. Legacy path: a single quantity.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [legacyQty, setLegacyQty] = useState(1);

  const totalQty = hasTiers
    ? Object.values(quantities).reduce((s, q) => s + (q || 0), 0)
    : legacyQty;
  const totalCents = hasTiers
    ? tierOrderTotalCents(tiers, quantities)
    : (ticketPriceCents ?? 0) * legacyQty;
  const total = (totalCents / 100).toFixed(2);
  const canPay = totalQty >= 1 && (hasTiers ? totalCents > 0 : (ticketPriceCents ?? 0) > 0);

  const onSubmit = async (data: TicketHolderData) => {
    const pageUrl = window.location.href.split('?')[0];
    try {
      const result = await checkoutMutation.mutateAsync({
        slug: schoolSlug,
        eventSlug,
        data: {
          holderName: data.holderName,
          holderEmail: data.holderEmail,
          ...(hasTiers
            ? { items: quantitiesToItems(quantities) }
            : { quantity: legacyQty }),
          successUrl: `${pageUrl}?success=1`,
          cancelUrl: pageUrl,
        },
      });
      window.location.href = (result as TicketCheckoutResult).url;
    } catch {
      // Error handled by mutation's onError snackbar
    }
  };

  return (
    <Paper
      sx={{
        p: 3,
        border: '2px solid',
        borderColor: '#FFCE00',
        bgcolor: 'rgba(255, 206, 0, 0.07)',
        boxShadow: 'none',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TicketIcon sx={{ color: '#FFCE00' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Buy Tickets
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {hasTiers
            ? tierPriceSummary(tiers)
            : `${formatCents(ticketPriceCents ?? 0)} per ticket`}
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="holderName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Name on ticket"
              fullWidth
              required
              sx={{ mb: 2 }}
              error={!!errors.holderName}
              helperText={errors.holderName?.message}
            />
          )}
        />

        <Controller
          name="holderEmail"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Email address"
              type="email"
              fullWidth
              required
              sx={{ mb: 2 }}
              error={!!errors.holderEmail}
              helperText={errors.holderEmail?.message || 'Your ticket will be sent here'}
            />
          )}
        />

        {hasTiers ? (
          <Box sx={{ mb: 3 }}>
            <TierQuantityPicker tiers={tiers} quantities={quantities} onChange={setQuantities} />
          </Box>
        ) : (
          <TextField
            label="Number of tickets"
            type="number"
            fullWidth
            value={legacyQty}
            inputProps={{ min: 1, max: 10 }}
            onChange={(e) =>
              setLegacyQty(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))
            }
            sx={{ mb: 3 }}
          />
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            px: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {totalQty} ticket{totalQty !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Total: ${total}
          </Typography>
        </Box>

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={!isValid || !canPay || checkoutMutation.isPending}
          sx={{ bgcolor: '#4580E4' }}
        >
          {checkoutMutation.isPending ? 'Redirecting to payment...' : `Pay $${total} →`}
        </Button>
      </Box>
    </Paper>
  );
}

// ===========================================
// PAGE COMPONENT
// ===========================================

export default function PublicEventDetailPage() {
  const { schoolSlug, eventSlug } = useParams<{ schoolSlug: string; eventSlug: string }>();
  const [searchParams] = useSearchParams();
  const effectiveSlug = schoolSlug || 'music-n-me';
  const effectiveEventSlug = eventSlug || '';

  const { data: event, isLoading, error } = usePublicEvent(effectiveSlug, effectiveEventSlug);

  const isSuccess = searchParams.get('success') === '1';

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          bgcolor: '#FCF6E6',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading event...</Typography>
      </Box>
    );
  }

  // Error / not found state
  if (error || !event) {
    return (
      <Box sx={{ bgcolor: '#FCF6E6', minHeight: '100vh', py: 8 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            Event not found. Please check the URL and try again.
          </Alert>
          <Button
            component={RouterLink}
            to={`/${effectiveSlug}/events`}
            sx={{ mt: 2 }}
            startIcon={<ArrowBackIcon />}
          >
            Back to Events
          </Button>
        </Container>
      </Box>
    );
  }

  const typeColor = EVENT_TYPE_COLORS[event.type] ?? '#9DA5AF';
  const icsUrl = eventIcsUrl(effectiveSlug, event.slug);
  const googleCalUrl = googleCalendarEventUrl({
    title: event.title,
    start: event.startDateTime,
    end: event.endDateTime,
    details: event.description,
    location: event.Venue?.name || event.venueName || event.Location?.name || null,
  });
  const hasTierPricing = (event.EventTicketTier?.length ?? 0) > 0;
  const isPaid =
    !event.isFree &&
    (hasTierPricing || (event.ticketPriceCents != null && event.ticketPriceCents > 0));
  const isRegistration = event.isPublic && !isPaid;

  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Character decoration - left */}
      <Box
        component="img"
        src="/character.png"
        alt=""
        sx={{
          position: 'fixed',
          left: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: 'calc(10% - 50px)', md: 'calc(20% - 50px)' },
          width: { xs: 120, md: 160, lg: 200 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(-10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Character decoration - right */}
      <Box
        component="img"
        src="/character-mint.png"
        alt=""
        sx={{
          position: 'fixed',
          right: { xs: -60, md: -20, lg: 20 },
          bottom: { xs: '30%', md: '40%' },
          width: { xs: 100, md: 140, lg: 180 },
          opacity: 0.4,
          pointerEvents: 'none',
          transform: 'rotate(10deg)',
          display: { xs: 'none', sm: 'block' },
        }}
      />

      {/* Music notes - left */}
      <Box
        sx={{
          position: 'fixed',
          left: { xs: 10, md: 40 },
          top: 'calc(30% + 50px)',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Music notes - right */}
      <Box
        sx={{
          position: 'fixed',
          right: { xs: 10, md: 40 },
          top: '50%',
          width: { xs: 60, md: 80 },
          opacity: 0.3,
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <Box component="img" src="/music-notes-3.png" alt="" sx={{ width: '100%' }} />
      </Box>

      {/* Mobile header */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          pb: 2,
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{ maxWidth: 120, height: 'auto', mb: 2 }}
        />
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
            color: 'primary.main',
            textAlign: 'center',
            fontSize: '1.75rem',
          }}
        >
          {EVENT_TYPE_LABELS[event.type] ?? 'Event'}
        </Typography>
      </Box>

      {/* Desktop piano header */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          position: 'relative',
          width: '100%',
          mb: 3,
        }}
      >
        <Box
          component="img"
          src="/piano-header.png"
          alt="Piano keys"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <Box
          component="img"
          src="/music-n-me-logo.png"
          alt="Music 'n Me"
          sx={{
            position: 'absolute',
            top: { sm: 12, md: 16 },
            left: { sm: 12, md: 16 },
            maxWidth: { sm: 100, md: 120 },
            height: 'auto',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'rgba(8,8,8,0.45)',
            py: 1,
            px: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
              color: 'white',
              textAlign: 'center',
              fontSize: { sm: '2rem', md: '2.5rem' },
            }}
          >
            {event.title}
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 6, position: 'relative', zIndex: 1, flex: 1 }}>
        {/* Back link */}
        <Button
          component={RouterLink}
          to={`/${effectiveSlug}/events`}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3, color: 'text.secondary' }}
        >
          All Events
        </Button>

        {/* Stripe success return banner */}
        {isSuccess && (
          <Alert
            severity="success"
            icon={<CheckIcon />}
            sx={{ mb: 3 }}
          >
            Payment successful! Your ticket confirmation has been sent to your email.
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Left column — event details */}
          <Grid item xs={12} md={7}>
            {/* Featured image */}
            {event.featuredImage ? (
              <Box
                component="img"
                src={event.featuredImage}
                alt={event.title}
                sx={{
                  width: '100%',
                  borderRadius: 1,
                  mb: 3,
                  maxHeight: 360,
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 220,
                  borderRadius: 1,
                  bgcolor: typeColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <CalendarIcon sx={{ fontSize: 80, color: 'white', opacity: 0.5 }} />
              </Box>
            )}

            {/* Title (mobile) */}
            <Typography
              variant="h4"
              sx={{
                display: { sm: 'none' },
                fontWeight: 700,
                mb: 1,
              }}
            >
              {event.title}
            </Typography>

            {/* Meta chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label={EVENT_TYPE_LABELS[event.type] ?? 'Event'}
                size="small"
                sx={{
                  bgcolor: typeColor,
                  color: eventTypeChipTextColor(event.type),
                  fontWeight: 600,
                }}
              />
              {event.isFree ? (
                <Chip label="Free" size="small" color="success" variant="outlined" />
              ) : (
                (() => {
                  const summary =
                    tierPriceSummary(event.EventTicketTier) ??
                    (event.ticketPriceCents != null
                      ? `$${(event.ticketPriceCents / 100).toFixed(2)}`
                      : null);
                  return summary ? (
                    <Chip
                      label={summary}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ) : null;
                })()
              )}
            </Box>

            {/* Date/time + venue */}
            <List dense disablePadding sx={{ mb: 2 }}>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CalendarIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={formatFullDate(event.startDateTime, event.endDateTime)}
                />
              </ListItem>
              {(event.venueName || event.Location?.name || event.Venue?.name) && (
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LocationIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={event.Venue?.name || event.venueName || event.Location?.name}
                    secondary={event.Room?.name}
                  />
                </ListItem>
              )}
              {event.Venue && (
                <ListItem disableGutters sx={{ display: 'block' }}>
                  <Suspense fallback={null}>
                    <VenueMap
                      latitude={event.Venue.latitude}
                      longitude={event.Venue.longitude}
                      name={event.Venue.name}
                      address={formatAddress(event.Venue)}
                    />
                  </Suspense>
                </ListItem>
              )}
              {!event.Venue && event.Location && (
                <ListItem disableGutters sx={{ display: 'block' }}>
                  <Suspense fallback={null}>
                    <VenueMap
                      latitude={event.Location.latitude}
                      longitude={event.Location.longitude}
                      name={event.Location.name}
                      address={formatAddress({ ...event.Location, country: event.Location.country ?? '' })}
                    />
                  </Suspense>
                </ListItem>
              )}
              {event.capacity != null && (
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <EventAvailableIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={`Capacity: ${event.capacity} attendees`} />
                </ListItem>
              )}
              {event.registrationDeadline && (
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CalendarIcon fontSize="small" sx={{ color: '#ff4040' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Registration closes: ${format(parseISO(event.registrationDeadline), 'd MMMM yyyy')}`}
                    primaryTypographyProps={{ color: '#ff4040' }}
                  />
                </ListItem>
              )}
            </List>

            {/* Add to calendar */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              <Button
                component="a"
                href={googleCalUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<CalendarIcon />}
                size="small"
              >
                Google Calendar
              </Button>
              <Button
                component="a"
                href={icsUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<ShareIcon />}
                size="small"
              >
                Apple / Outlook (.ics)
              </Button>
            </Box>

            {/* Description */}
            {event.description && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  About this event
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
                >
                  {event.description}
                </Typography>
              </>
            )}
          </Grid>

          {/* Right column — registration / ticket form */}
          <Grid item xs={12} md={5}>
            {isSuccess ? (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '2px solid',
                  borderColor: 'success.main',
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                }}
              >
                <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Thank you!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your ticket has been confirmed. Check your inbox for your ticket details and receipt.
                </Typography>
                <Button
                  component={RouterLink}
                  to={`/${effectiveSlug}/events`}
                  variant="outlined"
                  sx={{ mt: 2 }}
                >
                  Browse more events
                </Button>
              </Paper>
            ) : isPaid ? (
              <TicketCheckoutForm
                schoolSlug={effectiveSlug}
                eventSlug={event.slug}
                tiers={event.EventTicketTier ?? []}
                ticketPriceCents={event.ticketPriceCents}
              />
            ) : isRegistration ? (
              <RegistrationForm schoolSlug={effectiveSlug} eventSlug={event.slug} />
            ) : (
              // Event exists but no public action (e.g. invite-only or no action configured)
              <Paper
                sx={{
                  p: 3,
                  border: '2px solid',
                  borderColor: 'rgba(0,0,0,0.08)',
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  textAlign: 'center',
                }}
              >
                <CalendarIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  {event.isFree ? 'Free admission' : 'Contact us for tickets'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  For more information about attending this event, please contact the school directly.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  <Button
                    component="a"
                    href={googleCalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    startIcon={<CalendarIcon />}
                  >
                    Google Calendar
                  </Button>
                  <Button
                    component="a"
                    href={icsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    startIcon={<ShareIcon />}
                  >
                    Apple / Outlook (.ics)
                  </Button>
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>

      <PublicFooter schoolSlug={effectiveSlug} />
    </Box>
  );
}
